import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Popover,
  Radio,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import * as React from "react";
import CheckboxList from "../common/CheckboxList";
import { propsToClassKey } from "@mui/styles";

interface Props {
  imTypes: [];
  insuranceTypeOptions: [];
  insurancePlanNameOptions: any[];
  programNames: [];
  patientLists: [];
  anchorEl: any;
  setAnchorEl: Function;
  tagListLength: number;
  checkedItems: any;
  setCheckedItems: Function;
  checkedInsuranceItems?: any;
  setCheckedInsuranceItems?: Function;
  checkedInsurancePlanNames?: any;
  setCheckedInsurancePlanNames?: Function;
  checkedProgramItems?: any;
  setCheckedProgramItems?: Function;
  patientAgeRangeSelected: boolean;
  setPatientAgeRangeSelected: Function;
  showOnlyPatientsInLivingFacility: boolean;
  setShowOnlyPatientsInLivingFacility: Function;
  minAge?: string;
  setMinAge?: Function;
  maxAge?: string;
  setMaxAge?: Function;
  checkedPatientList: any;
  setCheckedPatientList: Function;
  clearFilter: Function;
}

const FilterMenu: React.FC<Props> = ({
  imTypes,
  insuranceTypeOptions,
  insurancePlanNameOptions,
  programNames,
  patientLists,
  anchorEl,
  setAnchorEl,
  tagListLength,
  checkedItems,
  setCheckedItems,
  checkedInsuranceItems = [],
  checkedInsurancePlanNames = [],
  setCheckedInsurancePlanNames = () => {},
  setCheckedInsuranceItems = () => {},
  checkedProgramItems = [],
  setCheckedProgramItems = () => {},
  patientAgeRangeSelected,
  setPatientAgeRangeSelected,
  showOnlyPatientsInLivingFacility,
  setShowOnlyPatientsInLivingFacility,
  minAge = "",
  setMinAge = () => {},
  maxAge = "",
  setMaxAge = () => {},
  checkedPatientList,
  setCheckedPatientList,
  clearFilter,
}) => {
  const [imTypesExpanded, setImTypesExpanded] = React.useState(true);
  const [patientListExpanded, setPatientListExpanded] = React.useState(false);
  const [insuranceTypesExpanded, setInsuranceTypesExpanded] =
    React.useState(false);
  const [insurancePlanNamesExpanded, setInsurancePlanNamesExpanded] =
    React.useState(false);
  const [programNamesExpanded, setProgramNamesExpanded] = React.useState(false);
  const [patientFiltersExpanded, setPatientFiltersExpanded] =
    React.useState(false);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClearFilter = () => {
    clearFilter();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const accordionHeight = "20px";

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <div className="popover-filter-container">
        <div className="header-wrapper">
          <p className="header">Patient Filters</p>
          <button onClick={handleClearFilter} className="clear-all">
            <svg
              width="9"
              height="9"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                y="7.99902"
                width="8"
                height="8"
                rx="1"
                transform="rotate(-90 0 7.99902)"
                fill="#FF890A"
              />
              <path
                d="M2.13699 1.66585L1.66699 2.13585L3.53033 3.99919L1.66699 5.86252L2.13699 6.33252L4.00033 4.46919L5.86366 6.33252L6.33366 5.86252L4.47033 3.99919L6.33366 2.13585L5.86366 1.66585L4.00033 3.52919L2.13699 1.66585Z"
                fill="white"
              />
            </svg>
            <span>Clear All</span>
          </button>
        </div>

        <p className="tag-number">{tagListLength} existing immunization tags</p>

        <Accordion
          expanded={imTypesExpanded}
          onChange={() => setImTypesExpanded(!imTypesExpanded)}
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: imTypesExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Immunization Type</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 16px",
            }}
          >
            <CheckboxList
              items={imTypes.map((type) => ({ id: type, label: type }))}
              checkedItems={checkedItems}
              setCheckedItems={setCheckedItems}
              labelField="label"
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={patientListExpanded}
          onChange={() => setPatientListExpanded(!patientListExpanded)}
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: patientListExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Patient List</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 10px",
            }}
          >
            {patientLists.map((list, index) => {
              const handleSelection = (list) => {
                if (checkedPatientList === list) {
                  setCheckedPatientList("");
                } else {
                  setCheckedPatientList(list);
                }
              };

              return (
                <div className="multiple-checkbox" key={index}>
                  <div className="checkbox-row-containter no-margin-bottom">
                    <Radio
                      checked={checkedPatientList === list}
                      onClick={() => handleSelection(list)}
                      value={list}
                      name="radio-buttons"
                      inputProps={{ "aria-label": list }}
                      style={{
                        backgroundColor: "transparent",
                        cursor: "pointer",
                      }}
                      sx={{
                        color: "#A29D9B",
                        "& .MuiSvgIcon-root": {
                          fontSize: "12px",
                        },
                      }}
                    />
                    <p
                      className="option-text"
                      onClick={() => handleSelection(list)}
                      style={{ cursor: "pointer" }}
                    >
                      {list}
                    </p>
                  </div>
                </div>
              );
            })}
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={insuranceTypesExpanded}
          onChange={() => setInsuranceTypesExpanded(!insuranceTypesExpanded)}
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: insuranceTypesExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Insurance Type</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 16px",
            }}
          >
            <CheckboxList
              items={insuranceTypeOptions.map((type) => ({
                id: type,
                label: type,
              }))}
              checkedItems={checkedInsuranceItems}
              setCheckedItems={setCheckedInsuranceItems}
              labelField="label"
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={insurancePlanNamesExpanded}
          onChange={() =>
            setInsurancePlanNamesExpanded(!insurancePlanNamesExpanded)
          }
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: insurancePlanNamesExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Insurance Plans</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 16px",
            }}
          >
            <CheckboxList
              items={insurancePlanNameOptions.map((type) => ({
                id: type,
                label: type,
              }))}
              checkedItems={checkedInsurancePlanNames}
              setCheckedItems={setCheckedInsurancePlanNames}
              labelField="label"
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={programNamesExpanded}
          onChange={() => setProgramNamesExpanded(!programNamesExpanded)}
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: programNamesExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Program Names</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 16px",
            }}
          >
            <CheckboxList
              items={programNames.map((type) => ({ id: type, label: type }))}
              checkedItems={checkedProgramItems}
              setCheckedItems={setCheckedProgramItems}
              labelField="label"
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={patientFiltersExpanded}
          onChange={() => setPatientFiltersExpanded(!patientFiltersExpanded)}
          sx={{
            boxShadow: "none",
            border: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={null}
            sx={{
              padding: 0,
              minHeight: accordionHeight,
              "&.MuiAccordionSummary-root": {
                minHeight: accordionHeight,
              },
              "&.MuiAccordionSummary-gutters": {
                padding: 0,
                minHeight: accordionHeight,
              },
              "&.Mui-expanded": {
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                alignItems: "center",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-contentGutters": {
                marginBottom: "10px",
                minHeight: accordionHeight,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
                minHeight: accordionHeight,
                alignItems: "center",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                p: "0 20px 0 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: patientFiltersExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <ArrowDropDownIcon />
              </Box>
              <p className="collapse-list-header">Patient Filters</p>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: "#F8F8F8",
              p: "20px 20px 12px 16px",
            }}
          >
            <div className="multiple-checkbox">
              <div className="checkbox-row-container">
                <label className="option-text">
                  <input
                    type="checkbox"
                    onChange={(e) =>
                      setPatientAgeRangeSelected(e.target.checked)
                    }
                    checked={patientAgeRangeSelected}
                    className="checkbox"
                  />
                  Patient Age Range:
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "5px",
                    }}
                  >
                    <TextField
                      value={minAge}
                      onChange={(e) => {
                        const minAge = e.target.value.replace(/\D/g, "");
                        setMinAge(minAge);
                      }}
                      placeholder="16 y/o"
                      slotProps={{
                        input: {
                          style: { padding: 0, height: 16, fontSize: 8 },
                        },
                        htmlInput: { inputMode: "numeric", maxLength: 3 },
                      }}
                      sx={{
                        height: "16px",
                        width: 36,
                        backgroundColor: "#efe9e7 !important",
                        borderRadius: "5px",
                        "& .MuiOutlinedInput-root": {
                          height: 16,
                          "& fieldset": { border: "none", outline: "none" },
                          "&:hover fieldset": {
                            border: "none",
                            outline: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                            outline: "none",
                          },
                          "& input": {
                            fontSize: 8, // input text size
                            padding: "5px !important",
                            "&::placeholder": {
                              fontSize: 8, // placeholder size
                            },
                          },
                        },
                      }}
                    />
                    <span>-</span>
                    <TextField
                      value={maxAge}
                      onChange={(e) => {
                        const maxAge = e.target.value.replace(/\D/g, "");
                        setMaxAge(maxAge);
                      }}
                      placeholder="88 y/o"
                      slotProps={{
                        input: {
                          style: { padding: 0, height: 16, fontSize: 8 },
                        },
                        htmlInput: { inputMode: "numeric", maxLength: 3 },
                      }}
                      sx={{
                        height: "16px",
                        width: 36,
                        backgroundColor: "#efe9e7 !important",
                        borderRadius: "5px",
                        "& .MuiOutlinedInput-root": {
                          height: 16,
                          "& fieldset": { border: "none", outline: "none" },
                          "&:hover fieldset": {
                            border: "none",
                            outline: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                            outline: "none",
                          },
                          "& input": {
                            fontSize: 8, // input text size
                            padding: "5px !important",
                            "&::placeholder": {
                              fontSize: 8, // placeholder size
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </label>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={showOnlyPatientsInLivingFacility}
                onChange={(e) =>
                  setShowOnlyPatientsInLivingFacility(e.target.checked)
                }
                color="primary"
                sx={{ transform: "scale(0.4)", marginLeft: "-18px" }}
              />
              <Typography sx={{ fontSize: "10px", marginLeft: "-12px" }}>
                Show Only Patients in a Living Facility
              </Typography>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </Popover>
  );
};

export default FilterMenu;
